/**
 * @file LiveChecksNotice.tsx
 * @description Постоянная плашка «Сайт работает без: реквизитов продавца,
 * политики ПДн. [Заполнить реквизиты →]» (phase19.16, SB125 п. 5; партия
 * SB120, этап 2 — uiux-sb120-stage2 §4, TD-407 — и на «Сводке» кабинета).
 *
 *  - `LiveChecksNoticeView` — сама плашка по списку ошибок: текст
 *    `liveWithoutText`, ссылки — общий реестр `checkLinkMeta` (как у списка
 *    проверок окна публикации); на телефоне каждая ссылка — строка 48 px;
 *  - `LiveChecksNotice` — обёртка конструктора: сайт опубликован
 *    (`draft.published`), ошибки — из снимка «что изменено» (`changes.checks`),
 *    при открытом окне публикации не видна (там свой список);
 *  - `ShopLiveChecksNotice` (ShopLiveChecksNotice.tsx) — обёртка «Сводки»
 *    по `GET live-issues`.
 * Только проверки по данным магазина (`shopLevelErrors`, коды — shared
 * live-checks.ts): проверки черновика к опубликованному сайту не относятся.
 */

import { Link } from "react-router";

import { NoticeStrip } from "../ui/NoticeStrip";
import { useSiteView } from "./site-view-context";
import { useCurrentContext } from "../../auth/hooks";
import { useBuilderLink } from "../../hooks/site-builder/useBuilderLink";
import { liveWithoutText, shopLevelErrors } from "../../lib/site-view/publish-acknowledge";
import { checkLinkMeta } from "../../lib/site-view/publish-check-links";
import type { PublishCheck } from "../../lib/site-view/types";

/** Плашка по списку ошибок; пустой текст — ничего. */
export function LiveChecksNoticeView({ errors }: { errors: readonly PublishCheck[] }) {
  const slug = useCurrentContext()?.shop?.slug ?? "";
  const builder = useBuilderLink();
  const text = liveWithoutText(errors);
  if (!text) return null;

  // Одна ссылка на раздел: две проверки с одной целью — одна ссылка.
  const links = new Map<string, string>();
  for (const check of errors) {
    const meta = checkLinkMeta(check);
    if (meta) links.set(meta.to(slug, builder), meta.label);
  }

  return (
    <NoticeStrip tone="warning">
      <div className="flex flex-col lg:flex-row lg:flex-wrap lg:items-center lg:gap-x-3">
        <span>{text}</span>
        {[...links].map(([to, label]) => (
          <Link key={to} to={to} className="flex min-h-12 items-center font-medium text-primary hover:underline lg:min-h-0">
            {label} →
          </Link>
        ))}
      </div>
    </NoticeStrip>
  );
}

/** Плашка конструктора: по снимку «что изменено». */
export function LiveChecksNotice() {
  const view = useSiteView();
  // Окно публикации показывает свой список — плашка за ним не нужна.
  if (!view.draft?.published || view.dialog === "publish") return null;
  return <LiveChecksNoticeView errors={shopLevelErrors(view.changes?.checks)} />;
}
